/**
 * Runtime validation schemas (zod) for the domain model in entities.ts.
 *
 * API handlers validate untrusted input with these before touching DynamoDB.
 * Only the most commonly-validated shapes are defined here to start; expand as
 * handlers are implemented. Keep these in lockstep with entities.ts.
 */
import { z } from "zod";
export const entitlement = z.enum(["free", "paid"]);
export const optInPolicy = z.enum(["single", "double"]);
export const listVisibility = z.enum(["open", "closed"]);
export const listAccess = z.enum(["free", "paid"]);
export const cadence = z.enum(["one_off", "daily", "weekly", "biweekly", "monthly"]);
export const templateMode = z.literal("raw_html");
/**
 * Whether an admin save means "make a new one" or "change this one" (#347).
 *
 * The save routes were blind upserts keyed on a caller-chosen id, so "create a
 * newsletter" with a slug that was already taken silently REPLACED the live one
 * — reopening a closed list and wiping its Reply-To — and the console said
 * "Created". `create` now refuses an id that exists (409) and `update` refuses
 * one that does not, so a typo can never turn into an overwrite.
 *
 * Called `intent`, not `mode`, because templates already carry a `mode` (the
 * authoring format).
 */
export const saveIntent = z.enum(["create", "update"]);
/**
 * Reusable address validator. RFC 5321 bounds an address at 254 octets, and it
 * matters here: `Subscriber.email` becomes the Cognito `Username` when an
 * account is provisioned, so anything wider fails at the directory instead of
 * at our boundary. Shared so adapters validate identically to ingest rather
 * than hand-rolling a regex (see redos.test.ts for why we don't).
 */
export const emailSchema = z.string().email().max(254);
/**
 * A merge tag as the renderer matches it — `{{first_name}}`, `{{ x }}` (#365).
 * The same pattern as `applyMerge` in `packages/domain/src/render.ts`, so "the
 * subject has a tag" means exactly "the body would have substituted this".
 * No `g` flag: a global regex carries `lastIndex` between `.test()` calls.
 */
export const MERGE_TAG_PATTERN = /\{\{\s*(\w+)\s*\}\}/;
/** Shown verbatim by the API and both editors, so the operator reads one sentence. */
export const SUBJECT_MERGE_TAG_MESSAGE = "Subjects can't contain merge tags: they are sent as typed, not personalised.";
/** Does this subject carry a merge tag the send path would NOT substitute? (#365) */
export function subjectHasMergeTag(subject) {
    return MERGE_TAG_PATTERN.test(subject);
}
/**
 * Every subject an operator types (#365).
 *
 * The body and preheader go through merge substitution; the subject goes to SES
 * as typed. So `Hi {{first_name}}` reached every inbox with the braces in it,
 * and nothing said so — the preheader in the same form IS merged, which makes
 * assuming the subject is too entirely reasonable. Refused rather than merged:
 * that is the product decision on the issue.
 *
 * A FIELD-level refine, not an object one, so `updateNewsletterContentSchema`'s
 * `.omit().extend()` keeps inheriting it.
 */
export const subjectSchema = z
    .string()
    .min(1)
    .refine((s) => !subjectHasMergeTag(s), { message: SUBJECT_MERGE_TAG_MESSAGE });
/**
 * A tenant-supplied identifier — `orgId`, `listId`, `campaignId`, `segmentId`,
 * `templateId`, `sequenceId` (#196).
 *
 * DynamoDB is not the reason for this. The key design there is sound: composite
 * partitions have disjoint sort-key namespaces, so no cross-tenant item
 * collision was constructible. These ids leak into OTHER namespaces that are not
 * prefix-disjoint and are not ours — EventBridge Scheduler names, S3 keys,
 * Secrets Manager names, OpenSearch indices, the magic-link `issuer`, and the
 * send-claim key. `z.string().min(1)` let `#`, `/`, `:` and whitespace through
 * to all of them.
 *
 * The charset deliberately excludes `.`, `#` and `/`, which is what makes those
 * three usable as unambiguous delimiters downstream — see `scheduleName` and the
 * send-claim key. Widening this charset silently reintroduces every collision it
 * closes, so it is the single place to change and the tests say so.
 *
 * 64 characters is the EventBridge Scheduler name limit less its prefix, and
 * comfortably under every other limit in play.
 */
export const idSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, "must be lowercase a-z, 0-9, `_` or `-`, and start with a letter or digit");
/**
 * An import batch id (#242).
 *
 * Validated because it is a KEY in two systems: it becomes the S3 object key the
 * async job reads (`imports/<org>/<batchId>`) and the sort key of the batch
 * record. Unvalidated, a caller could put `../`, control characters or an
 * unbounded string into both. S3 treats keys as opaque bytes so this never
 * escaped the org prefix at the storage layer — but the segment lands in a URL
 * PATH, where an intermediary normalising `..` would turn a same-tenant nuisance
 * into a cross-tenant read, and the same value could clobber the status of any
 * batch in the caller's own org.
 *
 * Slightly wider than `idSchema` because the ids this route issues carry an ISO
 * timestamp: `imp_2026-07-30T12:00:00.000Z_ab12cd34`.
 */
export const batchIdSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/, "must be alphanumeric with `.`, `_`, `:` or `-`, and start with a letter or digit");
/**
 * Subscriber attributes (#196).
 *
 * `z.record(z.string(), z.string())` bounded nothing, and `POST /signup` is
 * unauthenticated — so an anonymous caller could write a subscriber item up to
 * DynamoDB's 400 KB ceiling, once per address, and every later read of that
 * subscriber pays for it. The caps are generous for the real use (a handful of
 * merge tags: city, plan, first name) and cheap to raise deliberately.
 */
export const attributesSchema = z
    .record(z.string().min(1).max(64), z.string().max(1024))
    .refine((a) => Object.keys(a).length <= 32, {
    message: "at most 32 attributes",
});
export const consentSchema = z.object({
    timestamp: z.string().datetime(),
    ip: z.string(),
    sourceUrl: z.string().url(),
});
/** Public signup payload (unauthenticated, per §4.2). */
export const signupSchema = z.object({
    orgId: idSchema,
    email: z.string().email(),
    listId: idSchema,
    attributes: attributesSchema.optional(),
    sourceUrl: z.string().url().optional(),
});
/** Multi-list signup from the "All newsletters" page — one double opt-in covers all (§4.2). */
export const signupManySchema = z.object({
    orgId: idSchema,
    email: z.string().email(),
    // Bounded (#196): the handler walks these sequentially, so an
    // unauthenticated POST with 50,000 ids was 50,000 round-trips from one
    // request. Nobody signs up to more newsletters than an org publishes.
    listIds: z.array(idSchema).min(1).max(50),
    attributes: attributesSchema.optional(),
    sourceUrl: z.string().url().optional(),
});
/** Create-newsletter payload (admin). */
export const createListSchema = z.object({
    orgId: idSchema,
    listId: idSchema,
    name: z.string().min(1),
    description: z.string().optional(),
    sendFrequency: z.string().trim().optional(),
    category: z.enum(["News", "Business", "Classifieds", "Promotions"]).optional(),
    position: z.number().int().safe().optional(),
    optInPolicy,
    fromAddress: z.string().email(),
    replyTo: z.string().email().optional(),
    // NOT defaulted here (#347). A schema default is indistinguishable from the
    // caller saying "free"/"open", so an update that simply did not mention
    // visibility used to REOPEN a closed list. `saveList` applies the defaults
    // for a create and keeps the stored value for an update.
    access: listAccess.optional(),
    visibility: listVisibility.optional(),
    // CAN-SPAM: every list carries its compliance footer + physical address (§6).
    companyName: z.string().max(200).optional(),
    complianceFooter: z.string().min(1),
    physicalAddress: z.string().min(1),
    /** Absent means `create`: a caller that did not say "edit" must not overwrite (#347). */
    intent: saveIntent.optional(),
});
/** Save-campaign-draft payload (admin). */
export const saveCampaignSchema = z.object({
    orgId: idSchema,
    campaignId: idSchema,
    type: z.enum(["one_off", "series_edition"]),
    seriesId: z.string().optional(),
    subject: subjectSchema,
    previewText: z.string().optional(),
    templateId: idSchema,
    audience: z.object({ listId: idSchema.optional(), segmentId: idSchema.optional() }),
});
/**
 * One ad-slot fill as it arrives on a series payload (§4.16).
 *
 * `html` is inserted VERBATIM and never tracked (see `AdSlotFill`), so it is
 * deliberately not sanitized here — an ad tag is a third-party script snippet
 * whose whole value is being passed through untouched. That is why writing one
 * needs `campaigns:manage` rather than a weaker grant.
 *
 * `binding` is omitted from the wire shape on purpose: a fill saved through a
 * series route is series-bound by construction, and `saveCampaignSeries` stamps
 * the binding from the series it is being saved on. Accepting a caller-supplied
 * binding would let a payload claim a fill belongs to some OTHER series — a
 * cross-series write disguised as a field.
 */
export const adSlotFillSchema = z.object({
    slot: z.string().min(1).max(64),
    html: z.string().max(20000),
    version: z.number().int().nonnegative().default(1),
});
/**
 * Create or update a recurring campaign series (§4.6).
 *
 * `seriesId` is `idSchema` because it becomes a DynamoDB sort key
 * (`SERIES#<id>`) and an EventBridge schedule-name segment (`scheduleName`),
 * exactly the reasoning in #196.
 *
 * `adSlotFills` defaults to `[]` rather than being optional so that a caller
 * who omits it gets "no fills", not `undefined` — `CampaignSeries.adSlotFills`
 * is a required array and a stored `undefined` would break every reader.
 * Editing fills is a whole-array replace, which is why the Ad tags screen sends
 * the full set: a merge would make removing one fill impossible to express.
 */
export const saveCampaignSeriesSchema = z.object({
    orgId: idSchema,
    seriesId: idSchema,
    name: z.string().min(1).max(200),
    cadence,
    /**
     * Empty for a series made in Compose & schedule: its editions are built from
     * the HTML stored with the schedule, not a saved template. The domain accepts
     * "" only for such an existing series.
     */
    templateId: idSchema.or(z.literal("")),
    adSlotFills: z.array(adSlotFillSchema).max(20).default([]),
});
/** Create/update an RSS, Atom or JSON Feed source for recurring editions. */
export const saveFeedSchema = z.object({
    orgId: idSchema,
    feedId: idSchema,
    url: z.string().url().refine((value) => value.startsWith("https://"), "feed URL must use https"),
    format: z.enum(["rss", "atom", "json"]),
    targetListId: idSchema,
    fieldMap: z.record(z.string(), idSchema).default({}),
    pullIntervalMins: z.number().int().min(5).max(10080).default(60),
});
/**
 * Email body blocks (mirror `EmailTemplate`/`Block` in @addressium/domain's
 * renderer): text (may hold {{merge}} tags), a tracked editorial link, or an
 * ad slot inserted verbatim. Kept in lockstep with render.ts.
 */
/**
 * Link URL restricted to safe schemes (#94). `z.string().url()` alone accepts
 * `javascript:`/`data:` (the URL constructor treats them as valid), and editorial
 * links bypass the raw-HTML sanitizer in blocks mode — so a bad scheme would
 * reach the rendered `<a href>`. Restrict to http(s)/mailto at the boundary.
 */
const safeLinkUrl = z
    .string()
    .url()
    .refine((u) => /^(https?:|mailto:)/i.test(u.trim()), {
    message: "url must be http(s) or mailto",
});
export const emailBlockSchema = z.union([
    z.object({ kind: z.literal("text"), html: z.string() }),
    z.object({ kind: z.literal("editorial"), label: z.string().min(1), url: safeLinkUrl }),
    z.object({ kind: z.literal("ad"), slot: z.string().min(1), html: z.string() }),
]);
/** HTML/CSS is the authoring format; structured blocks remain readable for legacy callers. */
export const emailTemplateSchema = z.union([
    z.object({ blocks: z.array(emailBlockSchema).min(1) }),
    z.object({ html: z.string().min(1) }),
]);
/** Create/update a reusable HTML/CSS email template. */
export const saveTemplateSchema = z.object({
    orgId: idSchema,
    templateId: idSchema,
    name: z.string().min(1),
    mode: templateMode,
    source: z.string().min(1),
    mergeTags: z.array(z.string()).default([]),
    adSlots: z.array(z.string()).default([]),
    /**
     * Optional, and absent keeps the versioned upsert (#347): every save bumps
     * the version and older versions stay readable (#194), so an overwrite there
     * behaves like an edit. The console still says which it means, so "new
     * template" with a taken id is refused instead of becoming v2 of someone
     * else's template.
     */
    intent: saveIntent.optional(),
});
export const mergeTagSource = z.enum(["profile", "feed", "system", "token_claim"]);
export const mergeTagScope = z.enum(["per_recipient", "per_campaign", "token_claim"]);
/**
 * A merge-tag name as it appears inside `{{…}}`.
 *
 * Bounded for the same reason `idSchema` is (#196): the name becomes a DynamoDB
 * sort key (`MERGETAG#<name>`), so anything that can carry a `#` or whitespace
 * can shape the key rather than occupy it. Lowercase snake_case also matches
 * what every template in the repo already writes, so the bound costs an
 * operator nothing they were actually doing.
 *
 * Deliberately NOT where reserved names are refused — a `.refine` here becomes a
 * ZodError, which `fail()` answers with one generic sentence (#265), and
 * "unsubscribe_url is reserved" is precisely the sentence the operator needs.
 * `saveMergeTag` throws `InvalidInputError` instead.
 */
export const mergeTagNameSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, "merge tag names are lowercase letters, digits and underscores");
/** Register or update one org-defined merge tag (§4.15). */
export const saveMergeTagSchema = z.object({
    orgId: idSchema,
    name: mergeTagNameSchema,
    source: mergeTagSource,
    scope: mergeTagScope,
    example: z.string().max(200).optional(),
    fallback: z.string().max(200).optional(),
});
/** Remove one org-defined merge tag. Reserved names are refused in the domain, not here. */
export const deleteMergeTagSchema = z.object({ orgId: idSchema, name: mergeTagNameSchema });
/** Send-time editorial policy; all feeds and current content are required by default. */
export const newsletterPolicySchema = z.object({
    displayName: z.string().max(200).optional(),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
        const date = new Date(value + "T00:00:00Z");
        return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
    }, "invalid calendar date").optional(),
    adAudienceId: idSchema.optional(),
    categoryOneFeedId: idSchema.optional(),
    categoryTwoFeedId: idSchema.optional(),
    categoryOneName: z.string().max(100).optional(),
    categoryTwoName: z.string().max(100).optional(),
    failurePolicy: z.enum(["halt", "allow-partial"]).default("halt"),
    unchangedPolicy: z.enum(["hold", "warn", "allow"]).default("hold"),
    subjectMode: z.enum(["lead", "last", "newsletter", "fixed"]).default("lead"),
    marqueeSlot: idSchema.optional(),
    footerSlot: idSchema.optional(),
    bodySlots: z.array(idSchema).max(20).default([]),
});
/** Compose + schedule payload (§4.6): send now, at an instant, or recurring cron. */
export const scheduleCampaignSchema = z.object({
    orgId: idSchema,
    campaignId: idSchema,
    listId: idSchema,
    /**
     * Narrow the send to a segment's members (#203). The list is still required —
     * a segment targets WITHIN a list, and the list carries the from-address and
     * the CAN-SPAM footer every message needs.
     */
    segmentId: idSchema.optional(),
    /** Optional configured feed used to build each recurring edition. */
    feedId: idSchema.optional(),
    newsletter: newsletterPolicySchema.optional(),
    subject: subjectSchema,
    /**
     * Inbox preview line (#302). Rendered as a hidden div at the top of the body,
     * so it shows in the inbox list next to the subject and nowhere in the email.
     *
     * Capped because clients only display the first ~100-150 characters, and a
     * long one just pushes real content into the preheader slot.
     */
    previewText: z.string().max(200).optional(),
    template: emailTemplateSchema,
    /** Authoring format for reopening stored campaigns. */
    editorSource: z
        .object({
        mode: z.enum(["blocks", "html"]),
    })
        .optional(),
    /**
     * Replace a pending one-off with this campaign (#308).
     *
     * The old schedule is ARCHIVED, not paused: a paused one-off used to be
     * parked and re-enqueued on resume, so an operator resuming later would send
     * both versions. Archive is terminal and the sender's gate skips it.
     */
    supersedes: idSchema.optional(),
    when: z.union([
        z.object({ type: z.literal("now") }),
        z.object({ type: z.literal("at"), at: z.string().min(1) }),
        z.object({ type: z.literal("recurring"), cron: z.string().min(1), timezone: z.string().optional(), paused: z.boolean().optional() }),
    ]),
});
export const updateNewsletterContentSchema = scheduleCampaignSchema.omit({ when: true, supersedes: true }).extend({ expectedVersion: z.number().int().min(1) });
/** Send an immediate test copy of a draft or reviewed preview to 1–5 addresses. */
export const sendTestEmailSchema = z.object({
    orgId: idSchema,
    listId: idSchema,
    to: z.array(emailSchema).min(1).max(5),
    subject: subjectSchema.max(300),
    previewText: z.string().max(200).optional(),
    template: emailTemplateSchema,
});
/** Create/update-segment payload (admin). */
/**
 * Attribute names that must never be treated as a subscriber attribute (#195).
 *
 * `subscriber.attributes[field]` walks the prototype chain, so `constructor`
 * with `op: "exists"` returns `Object` — which is `!== undefined`, so the
 * condition matches EVERY subscriber. The engine also uses `Object.hasOwn` now;
 * rejecting these at save is the other half, so the segment cannot be stored in
 * the first place.
 */
const FORBIDDEN_FIELDS = ["__proto__", "constructor", "prototype"];
const segmentCondition = z
    .object({
    field: z.string().min(1).max(128),
    op: z.enum(["in", "eq", "neq", "exists", "before", "after"]),
    value: z.string().optional(),
})
    .refine((c) => !FORBIDDEN_FIELDS.includes(c.field), {
    message: "field name is reserved and would match every subscriber",
    path: ["field"],
})
    .refine((c) => c.op === "exists" || c.value !== undefined, {
    message: "value is required for every operator except `exists`",
    path: ["value"],
});
/**
 * A segment predicate (#195). `match` is REQUIRED, which is the whole point.
 *
 * It used to be `z.unknown()`, interpreted later as
 * `predicate.match === "all" ? every : some` — so a missing or misspelled
 * `match` fell through to `some`, and combined with `case "list": return true`
 * every subscriber in the base set matched. An operator saving "paid VIPs" with
 * a typo saw a plausible preview and mailed the entire list, including the
 * recipients the segment existed to exclude.
 */
export const rulePredicateSchema = z.object({
    match: z.enum(["all", "any"]),
    // Bounded: an unbounded condition list is an amplification vector on a
    // per-subscriber evaluation loop.
    conditions: z.array(segmentCondition).min(1).max(50),
});
/**
 * An explicitly-enumerated cohort (#203) — "these five addresses", not a rule.
 *
 * The rule engine is the right tool for an audience defined by a property, and
 * the wrong one for a hand-curated test cohort: expressing "exactly these five"
 * as conditions means inventing a marker attribute and hoping nobody else has
 * it. This kind resolves to its listed members and nothing else.
 *
 * Members are subscriber ids, not addresses. An address is mutable — a
 * subscriber who changes their email would silently leave the cohort — and
 * storing addresses here would put PII in a second place with its own erasure
 * path. The console resolves address → id at add time.
 *
 * 1000 is a test-cohort bound. A segment big enough to need more is a rule.
 */
export const explicitPredicateSchema = z.object({
    match: z.literal("explicit"),
    subscriberIds: z.array(z.string().min(1).max(64)).max(1000),
});
export const segmentPredicateSchema = z.union([rulePredicateSchema, explicitPredicateSchema]);
/** Add or remove one address from an explicit-membership segment (#203). */
export const segmentMemberSchema = z.object({
    orgId: idSchema,
    segmentId: idSchema,
    action: z.enum(["add", "remove"]),
    email: emailSchema,
});
export const saveSegmentSchema = z.object({
    orgId: idSchema,
    segmentId: idSchema,
    name: z.string().min(1),
    predicate: segmentPredicateSchema,
    /** Absent means `create` (#347) — see `saveIntent`. */
    intent: saveIntent.optional(),
});
/** Create/update drip-sequence payload (admin, #104). */
export const saveDripSequenceSchema = z.object({
    orgId: idSchema,
    sequenceId: idSchema,
    name: z.string().min(1),
    trigger: z.union([
        z.object({ kind: z.literal("signup"), listId: idSchema }),
        z.object({ kind: z.literal("manual") }),
    ]),
    steps: z
        .array(z.object({
        stepId: idSchema,
        waitSeconds: z.number().int().min(0),
        listId: idSchema,
        templateId: idSchema,
        subject: subjectSchema,
        requireEntitlement: entitlement.optional(),
    }))
        .min(1),
    /** Absent means `create` (#347) — see `saveIntent`. */
    intent: saveIntent.optional(),
    /** Absent keeps whatever the stored sequence has: re-saving steps never un-pauses (#368). */
    paused: z.boolean().optional(),
});
/**
 * Pause or resume a drip sequence without re-sending its steps (#368).
 *
 * Its own shape on the same `POST /drip-sequences` route rather than a flag on a
 * full save: a sequence whose stored step subject predates #365 would fail the
 * full schema, and the sequence with broken copy is exactly the one an operator
 * most needs to stop.
 */
export const pauseDripSequenceSchema = z.object({
    orgId: idSchema,
    sequenceId: idSchema,
    paused: z.boolean(),
});
/**
 * Enroll one subscriber into one manual-trigger drip sequence (admin, #245).
 *
 * `enrollmentId` is an optional idempotency key. Absent, the handler uses the
 * current instant, so two clicks of "Enroll" are two enrollments — which is the
 * honest reading of a deliberate operator action, and the opposite of the signup
 * path, where the identity is the subscriber's own opt-in request and a
 * triple-clicked confirmation link must collapse to one. A console that wants
 * click-safety sends a key.
 */
export const enrollDripSequenceSchema = z.object({
    orgId: idSchema,
    sequenceId: idSchema,
    subscriberId: z.string().min(1).max(64),
    enrollmentId: z.string().min(1).max(128).optional(),
});
/** Replace a subscriber's merge-tag attributes from the console (#205). */
export const setSubscriberAttributesSchema = z.object({
    orgId: idSchema,
    sub: z.string().min(1).max(64),
    attributes: attributesSchema,
});
/**
 * Set one subscription's status from the console (#205).
 *
 * `acknowledgeManualConfirmation` is required for `confirmed` and is enforced
 * again in the domain — a flag that only the client checks is not a safeguard.
 * `bounced` and `complained` are deliberately absent: those are facts SES
 * reports about a delivery, not states an operator asserts, and letting a human
 * type one in would corrupt the deliverability signal the halt logic reads.
 *
 * `pending` is absent too (#364). The console's "Invite (pending)" wrote a
 * pending row with no confirmation request behind it: no email went out, the
 * person could never confirm it, and it sat looking like an invite in progress
 * forever. Refused here so a stale client or a direct call cannot create one.
 */
export const setSubscriptionStatusSchema = z.object({
    orgId: idSchema,
    sub: z.string().min(1).max(64),
    listId: idSchema,
    status: z.enum(["confirmed", "unsubscribed"]),
    acknowledgeManualConfirmation: z.boolean().optional(),
});
/**
 * Manual suppression payload (admin, #247).
 *
 * `source` is optional and narrowed to the three an operator can meaningfully
 * pick: `manual` (no stated reason, stays org-scoped) or `bounce`/`complaint`
 * (an operator recording what SES would otherwise have told us — see
 * `scopeForSuppressionSource`, which is what actually decides the scope this
 * lands at). `unsubscribe` and `inactive` are system-written sources with no
 * "an admin typed this in" equivalent, so they are not offered here.
 */
export const manualSuppressSchema = z.object({
    orgId: idSchema,
    email: z.string().email(),
    source: z.enum(["manual", "bounce", "complaint"]).optional(),
});
/** Inbound entitlement sync from the billing system of record (§4.3). */
export const entitlementSyncSchema = z.object({
    orgId: idSchema,
    subscriberEmail: z.string().email(),
    entitlement,
    source: z.string().min(1),
    version: z.string().min(1),
    accessUntil: z.string().datetime().optional(),
});
/**
 * Inbound identity sync from the main user pool / system of record (§4.3).
 * One-directional (pool → addressium); addressium never writes back to the pool.
 * `externalId` is the immutable Cognito `sub`; email is a mutable attribute, so
 * an email change is an `upsert` with the same externalId and a new email.
 */
export const identitySyncSchema = z
    .object({
    orgId: idSchema,
    externalId: z.string().min(1),
    action: z.enum(["upsert", "delete"]).default("upsert"),
    email: z.string().email().optional(),
    attributes: attributesSchema.optional(),
    source: z.string().min(1).default("user-pool"),
})
    .refine((d) => d.action === "delete" || !!d.email, {
    message: "email is required for an upsert",
    path: ["email"],
});
/** Add-organization / provision-silo payload (§4.11). */
/**
 * Deliverability thresholds (#217, §4.18). `haltAt` must not sit below `warnAt`
 * — inverted thresholds would go critical before ever warning, which reads as a
 * mis-typed rule rather than an intended one.
 */
export const saveAlertConfigSchema = z.object({
    orgId: idSchema,
    snsTopicArn: z.string().optional(),
    rules: z
        .array(z
        .object({
        metric: z.enum(["complaint_rate", "bounce_rate", "send_failures", "reputation"]),
        warnAt: z.number().min(0),
        haltAt: z.number().min(0),
        enabled: z.boolean().default(true),
    })
        .refine((r) => r.haltAt >= r.warnAt, {
        message: "haltAt must be greater than or equal to warnAt",
        path: ["haltAt"],
    }))
        // An empty set is refused (#349): it reads back as a config, so the
        // console's "no thresholds" warning disappears while nothing can halt a
        // campaign. To stop halting, disable the rules instead — that is visible.
        .min(1, "at least one threshold rule is required; disable rules rather than removing them all")
        .max(16),
    notifyTargets: z.array(z.string()).max(32).default([]),
});
/** Per-organization engagement-based sunset / win-back policy. */
export const saveReengagementSchema = z
    .object({
    orgId: idSchema,
    enabled: z.boolean().default(false),
    coldAfterDays: z.number().int().min(1).max(3650),
    steps: z.number().int().min(1).max(10),
    stepIntervalDays: z.number().int().min(1).max(365),
    suppressScope: z.enum(["org", "global"]).default("org"),
    listId: idSchema.optional(),
})
    .refine((value) => !value.enabled || !!value.listId, {
    message: "listId is required when re-engagement is enabled",
    path: ["listId"],
});
/** Per-organization customer-record sync destination. */
export const saveCustomerSyncSchema = z.object({
    orgId: idSchema,
    endpoint: z.string().url().refine((value) => value.startsWith("https://"), "endpoint must use https"),
    tableName: z.string().min(1).max(255).regex(/^[A-Za-z0-9_.-]+$/, "table name may contain only letters, numbers, _, . or -"),
    secret: z.string().min(1).max(4096).optional(),
    enabled: z.boolean().default(true),
});
export const createOrgSchema = z
    .object({
    name: z.string().min(1),
    primaryDomain: z.string().min(1),
    siteDomain: z.string().min(1),
    region: z.string().default("us-east-1"),
    /** IANA time zone for recurring send scheduling + reporting (§4.16, §4.21). */
    defaultTimezone: z.string().default("UTC"),
    /**
     * LINK an existing Cognito user pool. There is no "create" mode: a pool
     * carries far more configuration than addressium can sensibly own, and it
     * is the operator's own directory (§4.10). Only meaningful with
     * `magicLinks` on — see the refinement below.
     */
    subscriberPool: z.object({ poolId: z.string().min(1) }).optional(),
    /**
     * Per-recipient magic-link tokens: a per-org KMS signing key, a published
     * JWKS, and a signed token in every editorial link. Off by default — an org
     * that only wants email sent needs no pool, no key and no entitlement
     * plumbing (§4.9).
     */
    magicLinks: z.boolean().default(false),
    /**
     * DMARC enforcement to publish (#200). Defaults to `none` — monitor only —
     * because turning on enforcement before you have read the aggregate reports
     * quarantines or rejects your own legitimate mail from senders you forgot
     * about. It is a starting point, not a resting point; the DNS guidance
     * returned by provisioning says so.
     */
    dmarcPolicy: z.enum(["none", "quarantine", "reject"]).default("none"),
    /**
     * An SES dedicated IP pool you have already created (#237). addressium does
     * not create pools — they are a standing charge and need a warm-up plan, so
     * provisioning one from a checkbox would bill you for infrastructure you did
     * not knowingly ask for. This REPLACES the old `dedicatedIp` boolean, which
     * set a field nothing read: no pool was ever created or assigned, so an org
     * marked "dedicated" sent on shared IPs with a record claiming otherwise.
     */
    dedicatedIpPoolName: z.string().min(1).optional(),
    suppressionScope: z.enum(["global", "org", "hybrid"]).default("hybrid"),
    /** `dev` marks a test silo (same workflows, labeled + excluded from cost rollups). */
    environment: z.enum(["prod", "dev"]).default("prod"),
    /** Dev-org send allowlist: exact emails or `@domain` suffixes. Fail-closed for dev orgs. */
    devAllowlist: z.array(z.string()).optional(),
    /**
     * SNS topic for deliverability breach notifications (#217). Optional — the
     * org gets default halt thresholds either way; without a topic the halt is
     * silent rather than absent.
     */
    alertTopicArn: z.string().optional(),
})
    // Pool present if and only if magic links are on. Enforced here, at the API
    // boundary, because neither half is any use without the other: a token has to
    // carry the pool's `sub` to be resolvable client-side, and a linked pool with
    // no tokens is a write to the operator's directory nobody asked for.
    .superRefine((v, ctx) => {
    if (v.magicLinks && !v.subscriberPool) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["subscriberPool"],
            message: "magic links require a linked subscriber pool (the token carries the pool's sub)",
        });
    }
    if (!v.magicLinks && v.subscriberPool) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["magicLinks"],
            message: "a subscriber pool is only used for magic links — set magicLinks: true or drop subscriberPool",
        });
    }
});
// ---- API keys (#280) ----
/**
 * A key's permission set (§4.12, #280). Mirrors `ApiKeyScope` in entities.ts as
 * a closed zod enum, so an unknown scope is refused at the boundary rather than
 * stored and silently never matched.
 */
export const apiKeyScope = z.enum([
    "subscribers:read",
    "subscribers:write",
    "entitlement:write",
    "campaigns:read",
    "suppression:write",
]);
/**
 * Issue one API key.
 *
 * `keyId` is `idSchema` for the same reason every other tenant id is: it becomes
 * a DynamoDB sort key (`APIKEY#<keyId>`), and an unvalidated one could shape the
 * key rather than occupy it.
 *
 * At least one scope is required. A key scoped to nothing is a credential that
 * authenticates and then may do nothing — it reads as a broken integration
 * rather than a deliberate one, exactly as `assertGrantable` argues for a member
 * scoped to no org. `.min(1)` here is a ZodError (a generic 400); the duplicate
 * check lives in `issueApiKey` as an `InvalidInputError` so the operator reads
 * WHICH scope repeated.
 */
export const issueApiKeySchema = z.object({
    orgId: idSchema,
    keyId: idSchema,
    name: z.string().min(1).max(120),
    scopes: z.array(apiKeyScope).min(1),
});
/** Revoke one API key. Revocation keeps the row — see `ApiKeyStore`. */
export const revokeApiKeySchema = z.object({ orgId: idSchema, keyId: idSchema });
/**
 * Generate or retire one of an org's inbound-webhook signing keys (#360). The
 * secret is never in a request: `generate` creates it server-side and returns
 * it once; `retire` names a key by its public id.
 */
export const webhookSecretActionSchema = z.discriminatedUnion("action", [
    z.object({ orgId: idSchema, action: z.literal("generate") }),
    z.object({ orgId: idSchema, action: z.literal("retire"), kid: z.string().regex(/^[0-9a-f]{12}$/) }),
]);
export const saveSettingsSchema = z.object({
    orgId: idSchema,
    hourlyEnabled: z.boolean(),
});
//# sourceMappingURL=schemas.js.map